import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import KendoDropDownHelper from '@/Utility/KendoDropDownHelper';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import Utilities from '@/Utility/Utilities';
import { IDropDownListViewModelConstructorParameter } from '#/DefaultViewExtensionInterfaces';

export default class DropDownListViewModel extends ControlViewModel {

    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');

    /**
     * Die wählbaren Optionen
     */
    public data: KnockoutObservableArray<any> | KnockoutComputed<any[]>;

    /**
     * Der ausgewählte Wert
     * Entspricht bei komplexen Typen dem Wert des "dataValueField"
     */
    public value: KnockoutObservable<any>;

    /**
     * Gibt an, ob es sich um ein DropDownList in der ToolBar handelt
     */
    public isToolBarLink: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Breite automatisch angepasst werden soll
     */
    public isAutoWidth: KnockoutObservable<boolean>;

    /**
     * Definiert die Höhe des DropDowns
     */
    public height: KnockoutObservable<number>;

    /**
     * Die Styles der DropDownList
     */
    public styles: KnockoutObservableArray<string>;

    /**
     * Das Feld, welches angezeigt wird
     */
    public dataTextField: string;

    /**
     * Das Feld, welches den Wert beinhaltet
     */
    public dataValueField: string;

    /**
     * Ein Template zur Formatierung des ausgewählten Objekts
     */
    public template: KnockoutObservable<any>;

    /**
     * Ein Template zur Formatierung der auswählbaren Objekte
     */
    public valueTemplate: KnockoutObservable<any>;

    /**
     * Das Label, welches angezeigt wird, wenn kein Wert ausgewählt ist
     */
    public optionLabel: KnockoutObservable<any>;

    /**
    * Wird aufgerufen, sobald ein Objekt selektiert wird
    */
    public onSelect: (selected: any) => void;

    /**
     * Wird aufgerufen, sobald ein Objekt deselektiert wird
     */
    public onUnselect: (unselected: any) => void;
    
    /**
     * Beinhaltet das derzeit selektierte Objekt
     */
    public selected: KnockoutComputed<any>;

    /**
     * Wird aufgerufen, sobald ein Objekt selektiert wird
     */
    public onSelectInternal: (event) => void;

    /**
     * Die Strategie, um Data-Locatoren für einzelne Einträge im Dropdown zu erstellen
     */
    public itemLocatorStrategy: (item: any, idx: number, $el: JQuery) => string | number;

    public isOpen: KnockoutObservable<boolean>;

    /**
     * Der ARIA-Label-Text für das Dropdown
     */
    public ariaLabel: string;

    public listContainerName: string;

    private onOpenCallback: () => unknown | JQueryPromise<unknown>;
    private onCloseCallback: () => unknown | JQueryPromise<unknown>;

    public constructor(protected params: IDropDownListViewModelConstructorParameter) {
        super(params);

        this.data = this.createData();
        this.value = this.createValue();
        this.isToolBarLink = this.observableOrDefault(params.isToolBarLink, false);
        this.isAutoWidth = this.observableOrDefault(params.isAutoWidth, false);
        this.height = this.observableOrDefault(params.height, 200);

        this.styles = this.observableOrDefault(params.styles, []) as KnockoutObservableArray<string>;
        this.dataTextField = this.paramOrDefault(params.dataTextField, null);
        this.dataValueField = this.paramOrDefault(params.dataValueField, null);
        this.template = this.observableOrDefault(params.template, null);
        this.valueTemplate = this.observableOrDefault(params.valueTemplate, null);
        this.optionLabel = this.observableOrDefault(params.optionLabel, '');

        this.onSelect = this.handlerOrDefault(params.onSelect);
        this.onUnselect = this.handlerOrDefault(params.onUnselect);
        this.onOpenCallback = this.handlerOrDefault(params.onOpen, () => { return Utilities.resolveImmediately() });
        this.onCloseCallback = this.handlerOrDefault(params.onClose);
        
        this.listContainerName = this.paramOrDefault(params.listContainerName, '');
        this.ariaLabel = this.paramOrDefault(params.ariaLabel, '');

        this.selected = this.defineComputed(() => {
            let value = this.value();
            let data = this.data();
            let findObject = {};

            findObject[this.dataValueField] = value;
            let object = _.findWhere(data, findObject);

            if (object == null) {
                findObject[this.dataValueField] = parseInt(value);
                object = _.findWhere(data, findObject);
            }

            return object;
        });

        this.onSelectInternal = event => {
            let data = this.data();
            let item = event.item;
            if (item == null) {
                return;
            }

            let index = item.index();

            if (data.length > 0 && data[index] != null) {
                let selected = data[index][this.dataValueField];

                if (selected && selected !== 'null') {
                    this.onSelect(selected);
                }
            }
        };

        this.itemLocatorStrategy = this.handlerOrDefault(params.itemLocatorStrategy);

        // Tooltip wird hier überschrieben 
        this.tooltip = new this.LabelViewModel({
            caption: KendoDropDownHelper.createComputedTooltip((params.tooltip ? params.tooltip.caption : ''),
                this.data, this.value, this.dataTextField, this.dataValueField, 
                (sub) => this.subscriptionCollection.push(sub))
        });

        this.isOpen = this.observableOrDefault(false);
    }

    /**
     * Creates data observable 
     */
    protected createData(): KnockoutObservableArray<any> | KnockoutComputed<any[]> {
        return this.observableOrDefault(this.params.data, []) as KnockoutObservableArray<any>;
    }

    /**
     * Creates value observable
     */
    protected createValue(): KnockoutObservable<any> {
        return this.observableOrDefault(this.params.value, null);
    }

    private onChangeInternal(event: any): void {
        let selected = event.sender.value();
        let previousSelected = this.value();
        let unselected = null;

        if (!selected) {
            unselected = previousSelected;
        }

        if (unselected && unselected !== 'null') {
            this.onUnselect(unselected);
        }

        this.onChange(selected);
    }

    public onChange(selected: any) {
        if (this.params.onChange) {
            this.params.onChange(selected);
        }
    }

    /**
     * Wird aufgerufen, sobald das DropDown geöffnet wird
     */
    public onOpen(): JQueryPromise<any> {
        return this.evaluate(this.onOpenCallback()).then(() => {
            this.isOpen(true);
        }).promise();
    }

    /**
     * Wird aufgerufen, sobald das DropDown geschlossen wird
     */
    public onClose(): JQueryPromise<any> {
        return this.evaluate(this.onCloseCallback()).then(() => {
            this.isOpen(false);
        }).promise();
    }

}

ComponentManager.setViewModel('DropDownListViewModel', DropDownListViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');